import { Input, Segmented, Divider } from "antd";
import { useParams } from "react-router-dom";
import { Crown, Search, Package } from "lucide-react";

import { useMemo, useState } from "react";
import { useAuth } from "../../contexts/Auth";
import { useTemplates } from "../../hooks/queries/templates";
import TemplateCard from "../../components/cards/TemplateCard";
import EmptyState from "../../components/EmptyState";
import CardGridSkeleton from "../../components/cards/CardGridSkeleton";

interface TemplatesProps {
}

const Templates = ({ }: TemplatesProps) => {
    const { projectId } = useParams() as { projectId: string; };
    const { isLoggedIn } = useAuth();
    const [searchQuery, setSearchQuery] = useState<string>("");
    const [filter, setFilter] = useState('microtica');

    // Use TanStack Query for templates
    const { data: templates = [], isLoading: loading } = useTemplates(projectId, isLoggedIn);

    // Filter templates using useMemo for performance
    const filteredTemplates = useMemo(() => {
        return templates.filter(template => {
            const typeCondition = filter === "microtica" 
                ? template.type === "template" || template.public
                : template.type === "component" && !template.public;
            
            const searchCondition = searchQuery 
                ? template.filterMetadata.toLowerCase().includes(searchQuery.toLowerCase())
                : true;

            return typeCondition && searchCondition;
        });
    }, [searchQuery, filter, templates]);

    const renderHeader = () => (
        <div style={{ 
            display: 'flex', 
            flexDirection: 'column',
            backgroundColor: "#ffffff",
            borderBottom: "1px solid #f0f0f0",
            flexShrink: 0,
            padding: "32px 50px 24px 50px"
        }}>
            <div style={{ 
                display: "flex", 
                alignItems: "center",
                justifyContent: "space-between",
                padding: "0",
                background: "transparent",
                gap: "32px"
            }}>
                <div style={{ display: "flex", alignItems: "center", gap: "24px", flex: 1 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "16px", flexShrink: 0 }}>
                        <div style={{ 
                            width: "48px", 
                            height: "48px", 
                            borderRadius: "12px", 
                            background: "linear-gradient(135deg, #077BB5 0%, #0a5a82 100%)", 
                            display: "flex", 
                            alignItems: "center", 
                            justifyContent: "center",
                            boxShadow: "0 4px 12px rgba(7, 123, 181, 0.2)"
                        }}>
                            <Package size={24} color="#fff" />
                        </div>
                        <div style={{ display: "flex", flexDirection: "column" }}>
                            <span style={{ fontSize: "20px", fontWeight: 600, color: "#111827", margin: "0 0 4px 0", letterSpacing: "-0.02em" }}>
                                Templates
                            </span>
                            <span style={{ fontSize: "14px", color: "#6b7280", margin: "0" }}>
                                Deploy production-ready applications in minutes
                            </span>
                        </div>
                    </div>

                    <Divider type="vertical" style={{ height: "32px", margin: "0 4px" }} />

                    <div style={{ flex: 1, maxWidth: "400px" }}>
                        <Input
                            placeholder="Search templates..."
                            prefix={<Search size={16} style={{ color: '#9CA3AF' }} />}
                            allowClear
                            size="large"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </div>
                </div>

                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    {projectId && (
                        <Segmented
                            options={[
                                { 
                                    label: (
                                        <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                            <Crown size={14} style={{ color: '#D97706' }} />
                                            Microtica Templates
                                        </span>
                                    ), 
                                    value: 'microtica'
                                },
                                { 
                                    label: 'My Templates', 
                                    value: 'my' 
                                },
                            ]}
                            value={filter}
                            onChange={(value) => setFilter(value as string)}
                        />
                    )}
                </div>
            </div>
        </div>
    );

    if (loading) {
        return (
            <div style={{ margin: 0, padding: 0, width: '100%', height: 'calc(100vh - 56px)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <div style={{ flex: 1, overflow: 'auto' }}>
                    {renderHeader()}
                    <div style={{ padding: '50px 50px' }}>
                        <CardGridSkeleton variant="template" count={8} />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div style={{ margin: 0, padding: 0, width: '100%', height: 'calc(100vh - 56px)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div style={{ flex: 1, overflow: 'auto' }}>
                {renderHeader()}

                {/* Content */}
                <div style={{ padding: '50px 50px' }}>
                    {filteredTemplates.length > 0 ? (
                        <div className="templates-grid">
                            {filteredTemplates.map((template, index) => (
                                <TemplateCard key={template.id || index} template={template} />
                            ))}
                        </div>
                    ) : (
                        <EmptyState
                            icon={<Search size={48} />}
                            title={searchQuery ? "No templates found" : "No templates available"}
                            description={
                                searchQuery 
                                    ? `No templates match "${searchQuery}". Try a different search term.`
                                    : filter === "my" 
                                        ? "You haven't created any custom templates yet."
                                        : "No templates are available for your current cloud provider."
                            }
                        />
                    )}
                </div>
            </div>
        </div>
    );
}

export default Templates;
